import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { cn } from '@/lib/utils'
import { band } from '@/constants/portfolio'
import { useFrame } from '@/hooks/use-frame'
import { completeAsset, registerAsset } from '@/lib/preload'
import { useExperience } from '@/components/generals/experience-provider'
import BrushWordmark from '@/components/landing/brush-wordmark'

import {
    clamp,
    documentTop,
    easeInOutCubic,
    easeOutCubic,
    lerp,
    range
} from '@/lib/motion'

import {
    BioPlate,
    BrushBand,
    FlatCrest,
    GenrePlate,
    HERO_SLOTS,
    HeroInstrument,
    HeroSummary,
    HeroTitle,
    ScrollHint
} from '@/components/landing/hero-parts'

const CrestCanvas = dynamic(() => import('@/components/landing/crest-canvas'), { ssr: false })

const HeroImmersive: FC = (): ReactNode => {
    const wrapRef = useRef<HTMLElement>(null)
    const progressRef = useRef(0)
    const crestRef = useRef<HTMLDivElement>(null)
    const flatSpinRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const summaryRef = useRef<HTMLDivElement>(null)
    const hintRef = useRef<HTMLDivElement>(null)
    const backdropRef = useRef<HTMLDivElement>(null)
    const bandRef = useRef<HTMLDivElement>(null)
    const plateRef = useRef<HTMLDivElement>(null)
    const slotRefs = useRef<Array<HTMLDivElement | null>>([])
    const genreRefs = useRef<Array<HTMLLIElement | null>>([])
    const measure = useRef({ at: -1, top: 0, travel: 1 })
    const lastKey = useRef('')
    const [webgl, setWebgl] = useState(true)
    const [crestReady, setCrestReady] = useState(false)

    useEffect(() => {
        registerAsset('crest', 2)
    }, [])

    const onReady = useCallback(() => {
        setCrestReady(true)
        completeAsset('crest')
    }, [])

    const onFallback = useCallback(() => {
        setWebgl(false)
        setCrestReady(false)
        completeAsset('crest')
    }, [])

    useFrame(state => {
        const wrap = wrapRef.current
        if (!wrap) return

        const vw = state.viewportW
        const vh = state.viewportH

        if (state.time - measure.current.at > 0.5) {
            measure.current = {
                at: state.time,
                top: documentTop(wrap),
                travel: Math.max(1, wrap.offsetHeight - vh)
            }
        }

        const { top, travel } = measure.current
        if (state.scrollY > top + travel + vh * 1.5) return

        const p = clamp((state.scrollY - top) / travel)
        progressRef.current = p

        const px = state.pointerX
        const py = state.pointerY
        const key = `${p.toFixed(4)}:${px.toFixed(3)}:${py.toFixed(3)}:${vw}:${vh}`
        if (key === lastKey.current) return
        lastKey.current = key

        const mobile = vw < 960

        // Crest: spins in WebGL (or CSS when flat), shrinks and slides aside.
        const c = easeInOutCubic(range(p, 0.04, 0.52))
        const crest = crestRef.current

        if (crest) {
            const x = mobile ? 0 : lerp(0, vw * 0.17, c)
            const y = mobile ? lerp(0, -vh * 0.18, c) : lerp(0, vh * 0.16, c)
            const s = lerp(1, mobile ? 0.5 : 0.44, c)
            crest.style.transform = `translate3d(${x + px * 10}px, ${y + py * 8}px, 0) scale(${s})`
            crest.style.opacity = mobile ? String(1 - range(p, 0.5, 0.62)) : '1'
        }

        const flat = flatSpinRef.current

        if (flat) {
            const spin = easeInOutCubic(range(p, 0.06, 0.7)) * 360
            flat.style.transform = `rotateY(${spin + px * 12}deg) rotateX(${-py * 12}deg)`
        }

        // Title, summary and scroll hint leave first.
        const t = range(p, 0.02, 0.36)

        if (titleRef.current) {
            titleRef.current.style.transform = `translate3d(${px * -6}px, ${t * vh * 0.3}px, 0)`
            titleRef.current.style.opacity = String(1 - easeOutCubic(range(p, 0.12, 0.38)))
        }

        if (summaryRef.current) {
            summaryRef.current.style.transform = `translate3d(0, ${-t * 40}px, 0)`
            summaryRef.current.style.opacity = String(1 - range(p, 0.02, 0.22))
        }

        if (hintRef.current) hintRef.current.style.opacity = String(1 - range(p, 0, 0.1))

        if (backdropRef.current) {
            backdropRef.current.style.transform = `translate3d(${-50 + px * -1.2}%, ${-50 + py * -1.2 - p * 12}%, 0) rotate(${-8 + p * 4}deg)`
        }

        // Members (their instruments) spread toward the edges at different depths.
        const spread = easeOutCubic(range(p, 0.03, 0.48))
        const fade = 1 - range(p, 0.36, 0.56)

        HERO_SLOTS.forEach((slot, index) => {
            const element = slotRefs.current[index]
            if (!element) return

            const [dx, dy] = slot.direction
            const x = dx * spread * vw * 0.22 + px * slot.depth * 18
            const y = dy * spread * vh * 0.5 + py * slot.depth * 12
            const r = dx * spread * 14
            element.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${r}deg)`
            element.style.opacity = String(fade)
        })

        // The white brush logo band slides in, then the genre plates, then the bio plate.
        const b = easeOutCubic(range(p, 0.3, 0.58))

        if (bandRef.current) {
            bandRef.current.style.transform = `translate3d(${lerp(-115, 0, b)}%, 0, 0) rotate(-4deg)`
        }

        genreRefs.current.forEach((element, index) => {
            if (!element) return

            const g = easeOutCubic(range(p, 0.44 + index * 0.035, 0.62 + index * 0.035))
            element.style.transform = `translate3d(${lerp(130, 0, g)}vw, 0, 0)`
        })

        const q = easeOutCubic(range(p, 0.58, 0.84))

        if (plateRef.current) {
            plateRef.current.style.transform = `translate3d(0, ${lerp(vh * 0.9, 0, q)}px, 0) rotate(${lerp(4, -0.6, q)}deg)`
            plateRef.current.style.visibility = q <= 0 ? 'hidden' : 'visible'
        }

        if (bandRef.current) {
            bandRef.current.style.opacity = mobile ? String(1 - range(p, 0.62, 0.78)) : '1'
        }
    })

    return (
        <section ref={wrapRef} id='top' data-pin-wrapper='true' aria-label='Introduction' className='relative h-[180vh] bg-paper tab:h-[260vh]'>
            <div className='sticky top-0 h-[100svh] overflow-hidden'>
                <div
                    ref={backdropRef}
                    aria-hidden='true'
                    className='pointer-events-none absolute left-1/2 top-1/2 w-[160vw] opacity-[0.06] will-change-transform'
                    style={{ transform: 'translate3d(-50%, -50%, 0) rotate(-8deg)' }}
                >
                    <BrushWordmark />
                </div>

                <div aria-hidden='true' className='halftone absolute -right-10 top-[12vh] h-[34vh] w-[30vw] -skew-x-12 opacity-[0.12]' />
                <div aria-hidden='true' className='halftone absolute -left-16 bottom-[20vh] h-[22vh] w-[26vw] -skew-x-12 opacity-[0.1]' />

                <HeroSummary innerRef={summaryRef} className='absolute left-5 top-[76px] z-20 will-change-transform xs:left-8 tab:left-12 tab:top-[104px]' />

                <ScrollHint innerRef={hintRef} className='absolute right-8 top-[110px] z-20 hidden tab:flex tab:right-12' />

                <HeroTitle innerRef={titleRef} className='absolute inset-x-0 bottom-[3vh] z-10 px-[1.2vw] will-change-transform' />

                {HERO_SLOTS.map((slot, index) => (
                    <HeroInstrument
                        key={slot.member.id}
                        slot={slot}
                        innerRef={element => {
                            slotRefs.current[index] = element
                        }}
                        className='z-20'
                    />
                ))}

                <div
                    ref={crestRef}
                    className='absolute bottom-[calc(3vh+6.6vw)] left-1/2 z-30 -ml-[min(38vw,24vh)] size-[min(76vw,48vh)] will-change-transform tab:-ml-[min(20vw,30vh)] tab:size-[min(40vw,60vh)]'
                >
                    <div className={cn('absolute inset-0 transition-opacity duration-500', webgl && crestReady ? 'opacity-0' : 'opacity-100')}>
                        <FlatCrest innerRef={webgl ? undefined : flatSpinRef} />
                    </div>

                    {webgl && (
                        <CrestCanvas
                            progressRef={progressRef}
                            onReady={onReady}
                            onFallback={onFallback}
                            className={cn('absolute -inset-[8%] transition-opacity duration-500', crestReady ? 'opacity-100' : 'opacity-0')}
                        />
                    )}
                </div>

                <BrushBand
                    innerRef={bandRef}
                    className='absolute left-[-4vw] right-[-4vw] top-[11vh] z-30 will-change-transform tab:top-[16vh]'
                />

                <ul className='absolute right-[4vw] top-[45vh] z-30 hidden flex-col items-end gap-3 tab:flex' aria-label='Genres'>
                    {band.genres.map((genre, index) => (
                        <GenrePlate
                            key={genre}
                            label={genre}
                            index={index}
                            innerRef={element => {
                                genreRefs.current[index] = element
                            }}
                        />
                    ))}
                </ul>

                <div className='absolute inset-x-4 bottom-[3vh] z-40 xs:inset-x-8 tab:bottom-[6vh] tab:left-12 tab:right-auto'>
                    <BioPlate innerRef={plateRef} pinned className='will-change-transform' />
                </div>
            </div>
        </section>
    )
}

const HeroStatic: FC = (): ReactNode => {
    useEffect(() => {
        completeAsset('crest')
    }, [])

    return (
        <section id='top' aria-label='Introduction' className='relative overflow-hidden bg-paper pb-16 pt-[88px] tab:pt-[112px]'>
            <div aria-hidden='true' className='pointer-events-none absolute left-1/2 top-[30%] w-[160vw] -translate-x-1/2 -rotate-[8deg] opacity-[0.06]'>
                <BrushWordmark />
            </div>

            <div className='relative px-5 xs:px-8 tab:px-12'>
                <HeroSummary />
            </div>

            <div className='relative mx-auto mt-10 flex max-w-[1440px] items-center justify-center gap-[4vw] px-5'>
                <div className='hidden w-[11vw] tab:block'>
                    <HeroInstrument slot={{ ...HERO_SLOTS[0], position: 'relative', width: 'w-full' }} className='relative' />
                </div>
                <div className='size-[min(72vw,440px)]'>
                    <FlatCrest />
                </div>
                <div className='hidden w-[11vw] tab:block'>
                    <HeroInstrument slot={{ ...HERO_SLOTS[1], position: 'relative', width: 'w-full' }} className='relative' />
                </div>
            </div>

            <HeroTitle className='relative mt-12 px-[1.2vw]' />

            <BrushBand className='relative mt-12 -rotate-2' />

            <ul className='mt-10 flex flex-wrap gap-3 px-5 xs:px-8 tab:px-12' aria-label='Genres'>
                {band.genres.map((genre, index) => (
                    <GenrePlate key={genre} label={genre} index={index} />
                ))}
            </ul>

            <div className='mt-12 px-5 xs:px-8 tab:px-12'>
                <BioPlate />
            </div>
        </section>
    )
}

const HeroSection: FC = (): ReactNode => {
    const { heroPinned } = useExperience()

    return heroPinned ? <HeroImmersive /> : <HeroStatic />
}

export default HeroSection
